import { defineStore } from "pinia";
import { ref, computed } from "vue";

/**
 * Format a Date object to MM-DD-YYYY string for URL
 */
const formatDateForUrl = (date) => {
  if (!date) return null;
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return null;
  const month = (d.getMonth() + 1).toString().padStart(2, "0");
  const day = d.getDate().toString().padStart(2, "0");
  const year = d.getFullYear();
  return `${month}-${day}-${year}`;
};

/**
 * Pinia store for All Patients page filter state.
 *
 * This store maintains filter state that persists during the session,
 * allowing navigation to detail pages and back while preserving filters.
 *
 * The store works alongside URL query parameters:
 * - URL params are the source of truth on page load
 * - Store syncs with URL params and maintains state during navigation
 * - Components use getNavigationQuery() to get params for router.push()
 */
export const useAllPatientsFiltersStore = defineStore("allPatientsFilters", () => {
  // Filter state
  const status = ref("ACTIVE");
  const patient = ref("");
  const date = ref(null);
  const sortBy = ref(null); // null means use default from client config

  // Computed: check if any non-default filters are active
  const hasActiveFilters = computed(() => {
    return status.value !== "ACTIVE" || (patient.value && patient.value.trim()) || date.value || sortBy.value;
  });

  /**
   * Update all filters at once (typically called when URL params change)
   */
  function setFilters({ status: newStatus, patient: newPatient, date: newDate, sortBy: newSortBy }) {
    if (newStatus !== undefined) {
      status.value = newStatus;
    }
    if (newPatient !== undefined) {
      patient.value = newPatient;
    }
    if (newDate !== undefined) {
      date.value = newDate;
    }
    if (newSortBy !== undefined) {
      sortBy.value = newSortBy;
    }
  }

  /**
   * Get query params object for navigation to all-patients page.
   * Only includes non-default values to keep URLs clean.
   */
  function getNavigationQuery() {
    const query = {};

    if (status.value && status.value !== "ACTIVE") {
      query.status = status.value;
    }

    if (patient.value && patient.value.trim()) {
      query.patient = patient.value.trim();
    }

    const formattedDate = formatDateForUrl(date.value);
    if (formattedDate) {
      query.date = formattedDate;
    }

    if (sortBy.value) {
      query.sort = sortBy.value;
    }

    return query;
  }

  /**
   * Clear all filters to defaults
   */
  function clearFilters() {
    status.value = "ACTIVE";
    patient.value = "";
    date.value = null;
    sortBy.value = null;
  }

  return {
    // State
    status,
    patient,
    date,
    sortBy,

    // Computed
    hasActiveFilters,

    // Actions
    setFilters,
    getNavigationQuery,
    clearFilters,
  };
});
